/**
 * Where the approved vocabulary appears in a procedure.
 *
 * THIS IS NOT A SPELL-CHECKER, and the splash used to say it would be. A page cannot add a word to
 * the browser's spell-checker -- there is no API for it -- so "a dictionary that stops it
 * underlining your part numbers" was a promise nothing could keep. The browser keeps doing its job;
 * this does the half that is both possible and worth more: it joins the words in the document to
 * the terms somebody approved in the Enterprise Glossary.
 *
 * WHY IT MATTERS IN A QMS. A glossary is the agreement about what the words mean. An SOP that uses
 * a term differently from the glossary is a document saying something nobody approved, and showing
 * the two together is how a reviewer notices.
 *
 * MATCHING IS DELIBERATELY CONSERVATIVE, because a false mark is worse than a missing one: a reader
 * who sees "Lens" lit up inside "Lenses" stops trusting every other mark on the page. So:
 *
 *   - WHOLE WORDS ONLY. A term must sit between non-word characters on both sides.
 *   - LONGEST FIRST. "Lens Element" claims its span before "Lens" can, or half the phrase goes
 *     unmarked and the reader sees a term that looks wrong. The server sorts for this too; the
 *     sort is repeated here because this file must be correct on whatever list it is handed.
 *   - NO OVERLAPS. Once a range is claimed nothing else may mark inside it.
 *   - NAMES CASE-INSENSITIVELY, ABBREVIATIONS NOT. "capa" in prose is a word somebody typed;
 *     "CAPA" is the abbreviation. Matching abbreviations case-insensitively lights up ordinary
 *     English, which is the quickest way to make somebody turn the feature off.
 */

export type GlossaryTerm = {
    Name: string;
    Abbreviation?: string | null;
    Definition?: string | null;
    Type?: string | null;
};

export type TermHit = {
    from: number;          // offset within the text that was searched
    to: number;
    term: GlossaryTerm;
    matched: string;       // what was actually written, which may differ in case from Name
};

/** Shortest term worth marking. "AV" inside "AVAILABLE" is not a use of the term, and the server
 *  applies the same floor to the names it sends -- this one is so the editor is right on its own. */
export const SHORTEST = 3;

/** An abbreviation may be shorter than a name, because that is the point of one. Two letters is
 *  the floor: a single letter between word boundaries is a bullet, a variable or a mistake. */
const SHORTEST_ABBREVIATION = 2;

/** Regex-safe. A term may legitimately contain a dot, a bracket or a plus -- "Mbed TLS 2.x",
 *  "Grade A+" -- and an unescaped one would either throw or match something else entirely. */
function quoted(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** The needles this term offers: its name, and its abbreviation when it has one. */
function needlesOf(term: GlossaryTerm): Array<{ text: string; fold: boolean }> {
    const out: Array<{ text: string; fold: boolean }> = [];
    const name = (term.Name || "").trim();
    if (name.length >= SHORTEST) out.push({ text: name, fold: true });

    const abbreviation = (term.Abbreviation || "").trim();
    // NOT WHEN IT IS THE NAME AGAIN -- a term abbreviated to itself would be looked for twice.
    if (abbreviation.length >= SHORTEST_ABBREVIATION
        && abbreviation.toLowerCase() !== name.toLowerCase()) {
        out.push({ text: abbreviation, fold: false });
    }
    return out;
}

/** Terms ordered the way matching needs them: longest name first, then alphabetically so the
 *  result does not depend on the order the server happened to return. */
export function sortedForMatching(terms: GlossaryTerm[]): GlossaryTerm[] {
    return [...terms].sort((a, b) => {
        const la = (a.Name || "").trim().length;
        const lb = (b.Name || "").trim().length;
        if (la !== lb) return lb - la;
        return (a.Name || "").localeCompare(b.Name || "");
    });
}

/**
 * ONE PASS OVER THE TEXT, NOT ONE PER TERM.
 *
 * The decorations are rebuilt whenever the document changes, so this runs while somebody is
 * typing. Looking for each term separately is a scan of the text per term -- five hundred terms
 * against a twenty-page procedure is felt in the keyboard -- so every needle goes into one
 * alternation and each text node is read once.
 *
 * ALTERNATION ORDER IS THE PRIORITY RULE: JavaScript takes the first alternative that matches at a
 * position, so ordering the needles longest first is what makes "Lens Element" win over "Lens".
 *
 * TWO EXPRESSIONS, because the folding differs -- names match whatever their case, abbreviations
 * do not. Built once per vocabulary and cached against the array it came from, since the terms
 * change once per document open and the text changes constantly.
 */
type Compiled = {
    names: RegExp | null;
    abbreviations: RegExp | null;
    byNeedle: Map<string, GlossaryTerm>;
};

const compiled = new WeakMap<object, Compiled>();

function alternation(needles: string[], flags: string): RegExp | null {
    if (!needles.length) return null;
    // LOOKAROUNDS RATHER THAN \b, which is defined against word characters: a term may begin or
    // end with one that is not, and "Grade A+" would never match on its right edge.
    return new RegExp(`(?<![A-Za-z0-9_])(?:${needles.map(quoted).join("|")})(?![A-Za-z0-9_])`, flags);
}

function compile(terms: GlossaryTerm[]): Compiled {
    const cached = compiled.get(terms as unknown as object);
    if (cached) return cached;

    const byNeedle = new Map<string, GlossaryTerm>();
    const names: string[] = [];
    const abbreviations: string[] = [];

    for (const term of sortedForMatching(terms)) {
        for (const needle of needlesOf(term)) {
            const key = needle.fold ? needle.text.toLowerCase() : needle.text;
            // FIRST ONE WINS, so two terms sharing a spelling resolve to the one the sort put
            // first rather than to whichever the server happened to send last.
            if (byNeedle.has(key)) continue;
            byNeedle.set(key, term);
            (needle.fold ? names : abbreviations).push(needle.text);
        }
    }

    const built: Compiled = {
        names: alternation(names, "gi"),
        abbreviations: alternation(abbreviations, "g"),
        byNeedle,
    };
    try { compiled.set(terms as unknown as object, built); } catch { /* not cacheable */ }
    return built;
}

/**
 * Every approved term used in this text, as ranges that do not overlap.
 *
 * Returned in document order, which is what a decoration pass wants, even though the longest match
 * is the one that claims a span.
 */
export function findTerms(text: string, terms: GlossaryTerm[]): TermHit[] {
    if (!text || !terms?.length) return [];

    const { names, abbreviations, byNeedle } = compile(terms);
    const found: TermHit[] = [];

    const sweep = (expression: RegExp | null, fold: boolean) => {
        if (!expression) return;
        expression.lastIndex = 0;
        let match = expression.exec(text);
        while (match) {
            const term = byNeedle.get(fold ? match[0].toLowerCase() : match[0]);
            if (term) {
                found.push({ from: match.index, to: match.index + match[0].length,
                             term, matched: match[0] });
            }
            match = expression.exec(text);
        }
    };

    sweep(names, true);
    sweep(abbreviations, false);

    // LONGEST FIRST, THEN LEFTMOST. The two expressions are swept separately, so an abbreviation
    // can land inside a name's range, and this is where that is settled.
    found.sort((a, b) => (b.to - b.from) - (a.to - a.from) || a.from - b.from);

    const claimed: Array<[number, number]> = [];
    const kept = found.filter(hit => {
        if (claimed.some(([a, b]) => hit.from < b && hit.to > a)) return false;
        claimed.push([hit.from, hit.to]);
        return true;
    });

    return kept.sort((a, b) => a.from - b.from);
}

/** What the reader is shown when they rest on a marked word. The definition is the point; the
 *  name is included because the written form may be an abbreviation or a different case. */
export function tooltipFor(term: GlossaryTerm): string {
    const name = (term.Name || "").trim();
    const definition = (term.Definition || "").trim();
    if (!definition) return `${name} — approved term, no definition recorded`;
    return `${name} — ${definition}`;
}
